import { type PlanDraft, type PublishBody, type PublishedWeek, type Row, validRows } from "@weights/plan-reading";
import { ApiError, AuthError, api, type Me, type Person } from "../api/client.js";
import { afterPublish, currentWeeks, invalidate, loadGroups, loadPlans, loadPublished, plansKey, revalidate, groupsKey, signedInMe } from "../portal/data.js";
import type { SavedGroup, TeamAthlete } from "./model/groups.js";
import type { HelperFrame, HelperRequest } from "./model/helperTypes.js";
import type { WeekAssistantRequest } from "./model/weekAssistantTypes.js";
import { type DefinitionReading, forServer, isReading } from "./model/reading.js";
import { fold } from "./model/text.js";

/** DESIGN §17.5, as the builder calls it. */

export interface PlanSummary {
  week: string;
  title: string;
  updatedAt: string;
  revision: number;
  publishedAt?: string;
  /** The draft differs from what was last published from it. */
  changedSincePublish?: boolean;
  /** Kept for reference (§17.10): never saved, published or deleted. */
  lockedAt?: string;
}

export interface StoredPlan {
  draft: PlanDraft;
  revision: number;
  updatedAt: string;
  publishedAt?: string;
  /** The draft as it was last published (§17.5). */
  published?: PlanDraft;
  lockedAt?: string;
}

export interface LibraryDefinition {
  label: string;
  lines: string[];
  week: string;
  title: string;
  /** Reuse copies it with the lines; the Worker reads again at save and keeps the coach's picks (§61.3). */
  reading?: DefinitionReading;
}

export interface PublishedSummary {
  week: string;
  sessions: number;
  updatedAt: string;
}

/** Who the shell signed in, without asking again; asked when the shell doesn't know yet. */
export const getMe = (): Promise<Me> => {
  const known = signedInMe();
  return known ? Promise.resolve(known) : api<Me>("/web/api/me");
};
/** Plan titles, kept with the portal's reads (DESIGN §26.1); none for a coach who doesn't plan. */
export const listPlans = () => loadPlans(readPlans);
/** The request itself, for a reader that keeps it under `plansKey` (as `useResource` does). */
export const readPlans = () => api<{ plans: PlanSummary[] }>("/web/api/plans").then((r) => r.plans ?? []);
/** Drafts are never kept here: `useDraftDoc` owns each one and its revision. */
export const getPlan = (monday: string) => api<StoredPlan>(`/web/api/plans/${monday}`);
/** The answer carries the draft as saved, each definition with the reading the Worker made of it (§61.3). */
export const savePlan = (monday: string, draft: PlanDraft, revision: number) =>
  api<{ revision: number; updatedAt: string; draft?: PlanDraft }>(`/web/api/plans/${monday}`, { method: "PUT", body: { draft: forServer(draft), revision } }).then((r) => {
    invalidate(plansKey);
    return r;
  });
/** §61.3: the Worker's reading of lines being edited, nothing stored; `prior` keeps the coach's picks. */
export async function readLines(label: string, lines: string[], prior?: DefinitionReading, signal?: AbortSignal): Promise<DefinitionReading> {
  const body: { label: string; lines: string[]; prior?: DefinitionReading } = { label, lines };
  if (prior) {
    const { readFrom: _, ...rest } = prior;
    body.prior = rest;
  }
  const r = await api<{ reading?: unknown }>("/web/api/plans/read", { method: "POST", body, signal });
  if (!isReading(r?.reading)) throw new Error("The reading didn't come back.");
  return r.reading;
}
export const deletePlan = (monday: string) =>
  api<unknown>(`/web/api/plans/${monday}`, { method: "DELETE" }).then((r) => {
    invalidate(plansKey);
    return r;
  });
/** `updateStarted` sends the changes to workouts already started too (§15.2). */
export const publishPlan = (monday: string, revision: number, updateStarted = false) =>
  api<PublishBody>(`/web/api/plans/${monday}/publish`, { method: "POST", body: updateStarted ? { revision, updateStarted } : { revision } }).then((r) => {
    afterPublish(monday);
    return r;
  });
export interface WeekNote {
  /** "" for the whole week. */
  columnID: string;
  body: string;
  updatedAt: string | null;
}
/** §17.12: the planner's own notes on a week, kept apart from the draft and never published. */
export const getWeekNotes = (monday: string) => api<{ notes: WeekNote[] }>(`/web/api/plans/${monday}/notes`).then((r) => r.notes ?? []);
export const saveWeekNote = (monday: string, columnID: string, body: string) =>
  api<WeekNote>(`/web/api/plans/${monday}/notes`, { method: "PUT", body: { columnID, body } });
/** §17.9: the team's past definitions, latest first; any failure is none. */
export async function searchLibrary(q: string, except: string, signal?: AbortSignal, limit = 50): Promise<LibraryDefinition[]> {
  const params = new URLSearchParams({ q, except, limit: String(limit) });
  try {
    const result = await api<{ definitions?: LibraryDefinition[] }>(`/web/api/plans/library?${params}`, { signal });
    return Array.isArray(result?.definitions) ? result.definitions : [];
  } catch (error) {
    if (error instanceof AuthError) throw error;
    return [];
  }
}
/** §26.3: the whole library, read once when the picker opens; `filterLibrary` narrows it as he types. */
export function loadLibrary(except: string, signal?: AbortSignal): Promise<LibraryDefinition[]> {
  return searchLibrary("", except, signal, LIBRARY_LIMIT);
}

/** The most the server returns at once (`LIBRARY_MAX` in plans.ts); a full page means there may be more. */
export const LIBRARY_LIMIT = 200;

/** The server's match (`q` in the label or the text, accents and case aside), on what was read. */
export function filterLibrary(definitions: LibraryDefinition[], query: string): LibraryDefinition[] {
  const q = fold(query);
  if (!q) return definitions;
  return definitions.filter((d) => fold(d.label).includes(q) || fold(d.lines.join("\n").trim()).includes(q));
}
/** §17.11: a week's past versions. */
export type VersionKind = "edited" | "published" | "deleted" | "replaced";
export interface VersionSummary {
  id: number;
  kind: VersionKind;
  title: string;
  savedAt: string;
  savedBy: string;
}
export const listVersions = (monday: string) => api<{ versions: VersionSummary[] }>(`/web/api/plans/${monday}/versions`).then((r) => r.versions ?? []);
export const getVersion = (monday: string, id: number) =>
  api<{ version: VersionSummary & { draft: PlanDraft } }>(`/web/api/plans/${monday}/versions/${id}`).then((r) => r.version);
export const restoreVersion = (monday: string, id: number, revision: number) =>
  api<{ revision: number; updatedAt: string }>(`/web/api/plans/${monday}/versions/${id}/restore`, { method: "POST", body: { revision } }).then((r) => {
    invalidate(plansKey);
    return r;
  });
/** The portal's weeks list, shared with it; any failure but signing out is none. */
export const listPublished = () => currentWeeks() as Promise<PublishedSummary[]>;
export const getPublished = (monday: string) => loadPublished(monday) as Promise<unknown> as Promise<PublishedWeek | null>;
export const listPeople = () => api<{ people: Person[] }>("/web/api/people").then((r) => r.people ?? []);
export const setCanPlan = (id: string, canPlan: boolean) =>
  api<{ person: Person }>(`/web/api/people/${id}`, { method: "PATCH", body: { canPlan } });

export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function isConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

export function messageOf(error: unknown): string {
  if (error instanceof AuthError) return "You were signed out. Sign in again from your phone.";
  if (error instanceof ApiError) return error.message;
  return "Couldn't reach the server. Check the connection and try again.";
}

/** A published week, or null when there is none (or it can't be read). */
export async function tryPublished(monday: string): Promise<PublishedWeek | null> {
  try {
    const week = await getPublished(monday);
    // A week never published comes back as one with no columns and no sessions, not as a 404.
    return week && (week.columns?.length || week.sessions?.length) ? week : null;
  } catch (error) {
    if (error instanceof AuthError) throw error;
    return null;
  }
}

export async function tryPlan(monday: string): Promise<StoredPlan | null> {
  try {
    return await getPlan(monday);
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
}

/** A line of his the rows don't carry as he wrote it (§29.1.1): its numbers aren't in its rows, or a row has numbers his line hasn't. */
export interface FitCheck {
  /** The index of his line, as his text splits by line. */
  line: number;
  kind: "numbers" | "invented";
  numbers: string[];
}

/** `from[i]` is the lines of his text `rows[i]` came from; lines the model dropped are back as written rows. */
export type FitResult = { rows: Row[]; from: number[][]; checks: FitCheck[]; model: string } | { rows: null; error: string };

const isIndex = (n: unknown, count: number): n is number => Number.isInteger(n) && (n as number) >= 0 && (n as number) < count;

/** §29.1.1: his text, for a model to read into rows; any failure, or an answer not in its shape, is an error, never a throw. */
export async function fitRows(label: string, text: string, signal?: AbortSignal): Promise<FitResult> {
  try {
    const result = await api<{ rows?: unknown; from?: unknown; checks?: unknown; model?: unknown; error?: unknown }>("/web/api/plans/fit", { method: "POST", body: { label, text }, signal });
    if (!Array.isArray(result?.rows)) return { rows: null, error: typeof result?.error === "string" ? result.error : "No rows came back" };
    const count = text.split("\n").length;
    const rows = validRows(result.rows);
    const from = result.from;
    if (!Array.isArray(from) || from.length !== rows.length || !from.every((f) => Array.isArray(f) && f.every((n) => isIndex(n, count)))) return { rows: null, error: "The rows came back without their lines" };
    if (!Array.isArray(result.checks)) return { rows: null, error: "The rows came back without their checks" };
    const checks: FitCheck[] = [];
    for (const c of result.checks as unknown[]) {
      const { line, kind, numbers } = (c ?? {}) as { line?: unknown; kind?: unknown; numbers?: unknown };
      if (!isIndex(line, count) || (kind !== "numbers" && kind !== "invented") || !Array.isArray(numbers) || !numbers.every((n) => typeof n === "string")) return { rows: null, error: "A check came back malformed" };
      checks.push({ line, kind, numbers: numbers as string[] });
    }
    return { rows, from: from as number[][], checks, model: typeof result.model === "string" ? result.model : "" };
  } catch (error) {
    return { rows: null, error: messageOf(error) };
  }
}

function asFrame(line: string): HelperFrame | null {
  let value: unknown;
  try {
    value = JSON.parse(line);
  } catch {
    return null;
  }
  const f = value as { t?: unknown; done?: unknown; model?: unknown; error?: unknown } | null;
  if (typeof f?.t === "string") return { t: f.t };
  if (f?.done === true) return { done: true, model: typeof f.model === "string" ? f.model : "" };
  if (typeof f?.error === "string") return { error: f.error === "timeout" || f.error === "no_key" ? f.error : "failed" };
  return null;
}

/**
 * DESIGN §34, §2.1: one question to the assistant, its reply as NDJSON frames as they arrive. Ends with a
 * `done` or an `error` frame: `too_many` for a 429, `offline` when it couldn't be reached, `cut` when the
 * stream stopped with neither. Signed out throws `AuthError`; an abort just ends it.
 */
export function askHelper(body: HelperRequest, signal?: AbortSignal): AsyncGenerator<HelperFrame> {
  return frames("/web/api/plans/helper", body, signal);
}

/** DESIGN §35.4: one question to the week assistant, its reply in the helper's frames, ended as `askHelper`'s. */
export function askWeek(body: WeekAssistantRequest, signal?: AbortSignal): AsyncGenerator<HelperFrame> {
  return frames("/web/api/plans/week-assistant", body, signal);
}

async function* frames(path: string, body: unknown, signal?: AbortSignal): AsyncGenerator<HelperFrame> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { accept: "application/x-ndjson", "content-type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
  } catch {
    if (!signal?.aborted) yield { error: "offline" };
    return;
  }
  if (response.status === 401) throw new AuthError();
  if (response.status === 429) {
    yield { error: "too_many" };
    return;
  }
  if (!response.ok || !response.body) {
    yield { error: "failed" };
    return;
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop()!;
      for (const line of lines) {
        const frame = line.trim() ? asFrame(line) : null;
        if (!frame) continue;
        yield frame;
        if (!("t" in frame)) return;
      }
    }
    buffer += decoder.decode();
    const last = buffer.trim() ? asFrame(buffer) : null;
    if (last) {
      yield last;
      if (!("t" in last)) return;
    }
    yield { error: "cut" };
  } catch {
    if (!signal?.aborted) yield { error: "cut" };
  } finally {
    reader.cancel().catch(() => {});
  }
}

// MARK: Groups (§17.8)

export interface GroupsData {
  groups: SavedGroup[];
  athletes: TeamAthlete[];
}

const byPosition = (groups: SavedGroup[]) => [...groups].sort((a, b) => a.position - b.position);

/** The team's saved groups and athletes, as last read: the Groups page reads them fresh with `getGroups`. */
export function cachedGroups(): Promise<GroupsData | null> {
  return loadGroups(getGroups);
}

/** The team's saved groups and athletes; null while the Worker has no groups yet (404). */
export async function getGroups(): Promise<GroupsData | null> {
  try {
    const result = await api<Partial<GroupsData>>("/web/api/groups");
    const groups = Array.isArray(result?.groups) ? result.groups.map((g) => ({ ...g, members: Array.isArray(g.members) ? g.members : [] })) : [];
    return { groups: byPosition(groups), athletes: Array.isArray(result?.athletes) ? result.athletes : [] };
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
}

/** For the builder, where groups are a convenience: any failure is none. */
export async function tryGroups(): Promise<GroupsData | null> {
  try {
    return await cachedGroups();
  } catch (error) {
    if (error instanceof AuthError) throw error;
    return null;
  }
}

function groupsChanged<T>(r: T): T {
  revalidate(groupsKey);
  return r;
}

export const saveGroup = (id: string, group: { name: string; members: string[] }) =>
  api<unknown>(`/web/api/groups/${encodeURIComponent(id)}`, { method: "PUT", body: group }).then(groupsChanged);
export const deleteGroup = (id: string) => api<unknown>(`/web/api/groups/${encodeURIComponent(id)}`, { method: "DELETE" }).then(groupsChanged);
export const orderGroups = (ids: string[]) => api<unknown>("/web/api/groups/order", { method: "PATCH", body: { ids } }).then(groupsChanged);
